import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';
import { SessionService } from '../../core/session/session.service';
import { ThemeService } from '../../core/theme/theme.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { HlmSpinnerComponent } from '../../ui';

/**
 * SMT tab — embeds the external Service Management Tool (SMT) via iframe and
 * drives it with the customer context of the current AMIE session (EB-3595).
 *
 * Integration replicates the old CMT pattern exactly (verified against
 * `service.management.tool.dir.js` and SMT `preload-page.component.ts`):
 *
 *   1. iframe loads `{smtUrl}preload/`.
 *   2. SMT posts `{type:'smt.loaded'}` to `window.parent` once the preload page
 *      is ready → we push `{type:'smt.reload', data:'customer-overview?…'}`.
 *   3. On a customer switch while the tab stays alive we re-arm via `preload`
 *      (the reload channel), which makes SMT re-emit `smt.loaded` and we push
 *      the new customer URL.
 *
 * Token hand-off (EB-3595, see features/.../smt-token-handoff.md): the iframe is
 * loaded with `?embed=amie`, and before every `smt.reload` we post
 * `{type:'smt.auth', token}` with AMIE's current Bearer token. SMT (in embed mode)
 * uses it instead of running its own OAuth — required because an embedded app
 * can't authenticate against a cross-site auth server (SameSite cookie withheld
 * in-frame + auth server is X-Frame-Options: DENY). Both apps share the same auth
 * server and scope, so AMIE's token is accepted by SMT's APIs.
 *
 * Message payloads are JSON strings on both ends. The token is passed via postMessage,
 * never in the iframe URL, and **never to a wildcard origin**: {@link post} targets
 * `this.smtOrigin` and returns early when that is unknown, while {@link handleMessage}
 * drops anything from a foreign origin. Both fail closed.
 *
 * This paragraph used to claim "origin is wildcard `'*'`" — false, and false in the
 * alarming direction: a reader would conclude the bearer token is broadcast to any
 * document occupying the frame (PR #42 review, roundup #10).
 *
 * NOTE (Pre-Flight §7): this page intentionally has no hero card — it is a
 * full-bleed embed of an external app, not a data page. Loading and error
 * states use the shared primitives (`hlm-spinner`, `cmt-error-state`).
 */
@Component({
  selector: 'cmt-smt',
  standalone: true,
  imports: [ErrorStateComponent, HlmSpinnerComponent],
  templateUrl: './smt.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmtComponent {
  readonly session = inject(SessionService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly oauthService = inject(OAuthService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  /** SMT's origin — the ONLY sender we accept embed messages from, and the exact
   *  targetOrigin we post the token to (never '*'). Null only if `smtUrl` is
   *  unparseable (EB-3595 review High 1 / Medium 1); a null origin fails closed —
   *  no inbound is trusted and nothing is posted (review run-2 Medium 2). */
  private readonly smtOrigin: string | null = (() => {
    try {
      return new URL(environment.smtUrl).origin;
    } catch {
      return null;
    }
  })();

  private readonly iframeRef = viewChild<ElementRef<HTMLIFrameElement>>('smtFrame');

  /** SMT reported `smt.loaded` — the preload page is ready to receive nav. */
  readonly loaded = signal(false);
  /** Iframe failed to load — show the fallback message. */
  readonly error = signal(false);

  /** Base preload URL (also asserted in tests). Trailing slash is in `smtUrl`. */
  readonly preloadUrl = environment.smtUrl + 'preload/';

  /** Preload URL with the embed flag + AMIE's active theme — signals SMT to run in
   *  AMIE-embed mode (accept the injected token, skip its own framed OAuth) and to
   *  apply the matching AMIE theme (EB-3804). Theme is captured once for the URL;
   *  later theme toggles are pushed via `smt.theme` (no reload — see constructor). */
  readonly embedUrl =
    `${this.preloadUrl}?embed=amie&style=${this.themeService.style().id}&mode=${this.themeService.mode()}`;

  /**
   * Fixed viewport height — the embed does NOT resize itself to SMT's content.
   *
   * Auto-height (EB-3595/EB-3804) sized the iframe from a `smt.height` message so
   * the AMIE page scrolled as one surface. SMT posts that message once per load
   * and never again, which broke both directions: content growing past the stale
   * height was unreachable, and content shrinking after a save left the page
   * scrolled into empty space (the "white screen" report). AMIE cannot observe
   * either across origins.
   *
   * Old CMT never had those symptoms because it never asked: a fixed `85vh`
   * iframe that scrolls internally (`service.management.tool.html:4-7`). This is
   * that behaviour, with the height adapted to AMIE's chrome. `smt.height` is
   * still accepted and recorded — it costs nothing and keeps the door open for a
   * future SMT that re-posts on change — but it no longer drives the layout.
   */
  readonly iframeHeight = computed(() => 'calc(100vh - 11rem)');

  /** Last height SMT reported. Recorded only; see {@link iframeHeight}. */
  readonly reportedHeight = signal<number | null>(null);

  /** Safety net: if SMT never posts `smt.loaded` at all — down, wrong URL, framing
   *  refused (X-Frame-Options / CSP `frame-ancestors`), or an HTTP error page — the
   *  iframe DOM `error` event does NOT fire, so without this the spinner would stick
   *  forever. Flip to the error state after a grace period instead (EB-3595 review
   *  run-2 Medium 1). Armed on every fresh load/re-arm, cleared on `smt.loaded`. */
  private static readonly LOAD_TIMEOUT_MS = 15000;
  private loadTimer: ReturnType<typeof setTimeout> | null = null;

  /** Reveal the iframe as soon as SMT reports itself loaded. With a fixed height
   *  there is no sizing transient left to hide behind the spinner. */
  readonly ready = computed(() => this.loaded());

  /**
   * The inner scrollbar stays available at all times.
   *
   * SMT reports `smt.height` once per load and does not re-post it when its own
   * content changes (measured 2026-09-04: `smt.loaded`, then a single
   * `smt.height`). With the scrollbar switched off, any content that later grows
   * past that stale height became unreachable — long language/service lists ended
   * mid-air with no way to scroll to them.
   *
   * With the fixed height above, this is how SMT is meant to be reached at all —
   * the same arrangement old CMT used.
   */
  readonly iframeScrolling = computed(() => 'auto');

  /** Bumped by retry() to force a fresh iframe navigation. */
  private readonly reloadCount = signal(0);

  /** Sanitized iframe src. A cache-busting suffix is added only on retry. */
  readonly iframeSrc = computed<SafeResourceUrl>(() => {
    const n = this.reloadCount();
    const url = n === 0 ? this.embedUrl : `${this.embedUrl}&_reload=${n}`;
    return this.domSanitizer.bypassSecurityTrustResourceUrl(url);
  });

  /** Last customer id we pushed to SMT — guards the re-arm effect. */
  private sentCustomerId: number | null = null;

  private readonly onMessage = (event: MessageEvent): void => this.handleMessage(event);

  constructor() {
    // EB-3798 / PR #43 review: no `side_entrance.click` here. This runs on every
    // route ACTIVATION (deep-link, back/forward, re-nav), not on a deliberate
    // click, and the navigation is already captured by `section.open`
    // (section: 'smt') in UsageTrackingService. A genuine click-anchored
    // side_entrance belongs on the nav affordance that jumps here, not the
    // embedded component's constructor.
    window.addEventListener('message', this.onMessage, false);
    this.armLoadTimeout(); // initial navigation — guard against SMT never loading
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('message', this.onMessage);
      this.clearLoadTimeout();
    });

    // Drive SMT reactively from (loaded ∧ customer). Centralising the push here
    // — rather than only inside the `smt.loaded` handler — fixes the async race
    // where `smt.loaded` arrives before SessionService has resolved the customer
    // (direct navigation / refresh on /:id/smt): SMT would otherwise sit idle on
    // its own spinner forever, waiting for a broadcast that never comes.
    effect(() => {
      const customer = this.session.customer();
      if (!this.loaded() || !customer) return;
      if (customer.id === this.sentCustomerId) return;

      if (this.sentCustomerId === null) {
        // First push for this SMT session (initial load, or late-arriving customer).
        // Hand off a fresh token BEFORE the reload so SMT has it before it
        // navigates to (and guards) the customer-overview route.
        this.postAuth();
        this.postToSmt(this.buildCustomerUrl());
        this.sentCustomerId = customer.id;
      } else {
        // Customer changed after a push → return SMT to preload; it re-emits
        // `smt.loaded`, this effect re-runs and pushes the new customer-overview.
        // Reset the sizing state so the spinner covers the switch's resize too.
        this.sentCustomerId = null;
        this.loaded.set(false);
        this.reportedHeight.set(null);
        this.armLoadTimeout(); // awaiting a fresh `smt.loaded` after the re-arm
        this.postToSmt('preload');
      }
    });

    // EB-3804: push AMIE theme changes to the embedded SMT live (no reload). The
    // initial theme also rides the iframe URL, so this only matters when the
    // operator toggles theme/mode while the SMT tab is open.
    // EB-4529: do NOT post before SMT reported `smt.loaded`. A freshly inserted
    // iframe still holds its initial blank document, which has AMIE's origin, so
    // the browser rejects a message targeted at the SMT origin (console warning)
    // and drops it. Reading `loaded()` here also re-posts the current theme once
    // SMT is up, so a toggle made while it was loading is not lost.
    effect(() => {
      const style = this.themeService.style().id;
      const mode = this.themeService.mode();
      if (!this.loaded()) return;
      this.post({ type: 'smt.theme', style, mode });
    });
  }

  /** Build the SMT customer-overview path from the current session customer. */
  buildCustomerUrl(): string {
    const customer = this.session.customer();
    if (!customer) return '';
    return `customer-overview?customerId=${customer.id}&brandId=${customer.brandId}&lang=${customer.mainCustomerLanguage}`;
  }

  /** Iframe load error → show fallback (ignored once SMT is already up). */
  onIframeError(): void {
    if (!this.loaded()) {
      this.error.set(true);
      this.clearLoadTimeout(); // already in the error state → the reveal timer is moot
    }
  }

  /** Reset and reload the iframe from preload. */
  retry(): void {
    this.error.set(false);
    this.loaded.set(false);
    this.reportedHeight.set(null);
    this.armLoadTimeout(); // fresh navigation — re-arm the never-loaded guard
    this.sentCustomerId = null;
    this.reloadCount.update((n) => n + 1);
  }

  /** Max iframe height we'll honour from `smt.height` (guards against a bad/spoofed
   *  value blowing up layout/memory — EB-3595 review Low 1). */
  private static readonly MAX_IFRAME_HEIGHT = 20000;

  /**
   * Handle a postMessage from the SMT iframe. Only accepts messages from the SMT
   * origin (EB-3595 review Medium 1) — ignores anything else. Non-SMT messages are
   * ignored. Only flips `loaded`; the customer push is driven reactively by the
   * effect so it also fires when the customer resolves after `smt.loaded`.
   */
  private handleMessage(event: MessageEvent): void {
    if (!this.smtOrigin || event.origin !== this.smtOrigin) {
      return; // only SMT (in its iframe) may drive this component; fail closed if the origin is unknown
    }
    let message: { type?: string; height?: unknown } | null;
    try {
      message = JSON.parse(event.data);
    } catch {
      return; // not our JSON envelope
    }
    if (!message) return;

    if (message.type === 'smt.loaded') {
      this.loaded.set(true);
      this.error.set(false);
      this.clearLoadTimeout(); // SMT responded → the never-loaded guard is moot
    } else if (message.type === 'smt.height' && typeof message.height === 'number' && message.height > 0) {
      // Embed auto-resize: SMT reports its content height → grow the iframe.
      this.reportedHeight.set(Math.min(Math.ceil(message.height), SmtComponent.MAX_IFRAME_HEIGHT));
    }
  }

  /** (Re-)arm the never-loaded guard (idempotent): if `smt.loaded` hasn't arrived
   *  by the deadline, show the error fallback instead of an eternal spinner. */
  private armLoadTimeout(): void {
    this.clearLoadTimeout();
    this.loadTimer = setTimeout(() => {
      if (!this.loaded()) this.error.set(true);
    }, SmtComponent.LOAD_TIMEOUT_MS);
  }

  private clearLoadTimeout(): void {
    if (this.loadTimer !== null) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  }

  /** Post `{type:'smt.reload', data:path}` to the iframe. */
  private postToSmt(path: string): void {
    this.post({ type: 'smt.reload', data: path });
  }

  /** Hand AMIE's current Bearer token to SMT so it can auth in embed mode.
   *  Includes the real expiry so SMT doesn't assume a fixed TTL and keep using a
   *  token past its life (EB-3595 review M4). No-op when there is no token. */
  private postAuth(): void {
    const token = this.oauthService.getAccessToken();
    if (!token) return;
    const expiresAt = this.oauthService.getAccessTokenExpiration();
    this.post(expiresAt ? { type: 'smt.auth', token, expiresAt } : { type: 'smt.auth', token });
  }

  /** Serialize a message to the SMT iframe, targeted at the SMT origin so the
   *  bearer token can never be delivered to a foreign document that happens to
   *  occupy the frame (EB-3595 review High 1). Fail closed if the origin is
   *  unknown — never fall back to `'*'` with the token in hand (review run-2
   *  Medium 2). */
  private post(message: object): void {
    if (!this.smtOrigin) return;
    const frame = this.iframeRef()?.nativeElement;
    frame?.contentWindow?.postMessage(JSON.stringify(message), this.smtOrigin);
  }
}
